import * as HLF from "html-living-framework";

// singletons
import { GameState } from "Models/GameState";

// library functions
import { Elastic } from "gsap/EasePack";
import { TimelineLite } from "gsap/TimelineLite";
import { TimelineMax } from "gsap/TimelineMax";
import { TweenLite } from "gsap/TweenLite";
import { TweenMax } from "gsap/TweenMaxBase";
import { makeSprite } from "html-living-framework/lib/Utils/PixiUtils";

// static data
import * as Constants from "Data/Constants";
import type { LocationID } from "Data/GameConfig";
import * as Topics from "Data/Topics";
import * as VO from "Data/VO";

// gameobjects
import { Application } from "Application";
import { CitySelectButton } from "GameObjects/CitySelect/CitySelectButton";

// shorthands
const fnt = Constants.FONT_GEL_MED_22;
const ss = ["cityselect-1"];
const clr = HLF.Colour.WHITE.toHex();
const btn = "button-continue";
const bgm = "LATW_1189_1_happy_pacer_jazz_New_Orleans_KK";
const IDLE_DELAY = 5;
const IDLE_REPEAT_DELAY = 10;

// shared between all instances
const voArray = [
    VO.UI.CITY_SELECT.UI2B,
    VO.UI.CITY_SELECT.UI3,
    VO.UI.CITY_SELECT.UI4,
    VO.UI.CITY_SELECT.UI5,
    VO.UI.CITY_SELECT.UI6,
];

// rotate VOs
const i = HLF.Random.intBetween(0, voArray.length);
voArray.unshift(...voArray.splice(-i));

export class CitySelect extends HLF.State {

    public static readonly Assets: HLF.AssetMapData[] = [
        new HLF.AssetMapData(fnt, HLF.AssetType.FONT),
        ...ss.map((s) => new HLF.AssetMapData(s, HLF.AssetType.TEXTURE_ATLAS)),
        new HLF.TextureAsset(
            "cityselect/luna_seek_cityselect_v2", "images/static/cityselect/luna_seek_cityselect_v2.jpg"),
        new HLF.AudioAsset("ui_select", HLF.AudioCategory.SFX),
        new HLF.AudioAsset("pluck", HLF.AudioCategory.SFX),
        new HLF.AudioAsset("pop-2", HLF.AudioCategory.SFX),
        new HLF.AudioAsset(bgm, HLF.AudioCategory.BGM),
        new HLF.AudioAsset(VO.UI.CITY_SELECT.UI2B, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.UI.CITY_SELECT.UI3, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.UI.CITY_SELECT.UI4, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.UI.CITY_SELECT.UI5, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.UI.CITY_SELECT.UI6, HLF.AudioCategory.VO),
    ];

    protected markers: PIXI.Container[] = [];
    protected stamps: PIXI.Container[] = [];
    protected swooshes: PIXI.Container[] = [];
    protected _subscriptions: any[] = [];
    private animateInTimeline?: TimelineLite;
    private idleTimeline?: TimelineMax;
    private firstTime: boolean = false;

    public init(pSize: PIXI.Point, pData?: {firstTime: boolean}) {
        super.init(pSize, pData); // calls onResize
        this.firstTime = pData?.firstTime ?? false;
        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);

        this.addChildAt(makeSprite("cityselect/luna_seek_cityselect_v2"), 0);

        this.swooshes.push(this.MakeButton("juneau-swoosh"));
        this.swooshes.push(this.MakeButton("paris-swoosh"));
        this.swooshes.push(this.MakeButton("rio-swoosh"));
        this.swooshes.push(this.MakeButton("london-swoosh"));
        this.swooshes.push(this.MakeButton("neworleans-swoosh"));
        this.swooshes.push(this.MakeButton("delhi-swoosh"));

        this.stamps.push(this.MakeButton("juneau-stamp", "juneau", VO.UI.CITY_SELECT.UI16ALT));
        this.markers.push(this.MakeButton("juneau-marker"));

        this.stamps.push(this.MakeButton("paris-stamp", "paris", VO.UI.CITY_SELECT.UI17));
        this.markers.push(this.MakeButton("paris-marker"));

        this.stamps.push(this.MakeButton("rio-stamp", "rio", VO.UI.CITY_SELECT.UI19));
        this.markers.push(this.MakeButton("rio-marker"));

        this.stamps.push(this.MakeButton("london-stamp", "london", VO.UI.CITY_SELECT.UI18ALT));
        this.markers.push(this.MakeButton("london-marker"));

        this.stamps.push(this.MakeButton("neworleans-stamp", "neworleans", VO.UI.CITY_SELECT.UI20ALT));
        this.markers.push(this.MakeButton("neworleans-marker"));

        this.stamps.push(this.MakeButton("delhi-stamp", "delhi", VO.UI.CITY_SELECT.UI20ALT));
        this.markers.push(this.MakeButton("delhi-marker"));

        this._subscriptions.push(
            PubSub.subscribe(Topics.RESTART_IDLE_TIMER, this.RestartIdleTimer.bind(this)),
            PubSub.subscribe(HLF.GAME_PAUSED, () => this.idleTimeline?.kill()),
            PubSub.subscribe(HLF.GAME_UNPAUSED, this.RestartIdleTimer.bind(this)),
        );
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        this.idleTimeline?.kill();
        super.destroy(pOptions);
    }

    public animateIn(pOnComplete: () => void) {
        pOnComplete();

        const onboarding = GameState.Instance.onboarding;
        if (onboarding.complete) {
            if (this.firstTime) {
                Application.instance.voiceover.playVO([VO.ONBOARDING.UI2, Constants.VO_SPACING], HLF.PlayMode.Append);
            }
            else {
                const voToPlay = voArray.shift();
                if (voToPlay !== undefined) {
                    voArray.push(voToPlay);
                    Application.instance.voiceover.playVO([voToPlay, Constants.VO_SPACING], HLF.PlayMode.Append);
                }
            }

            this.idleTimeline = new TimelineMax();
            this.idleTimeline.delay(IDLE_DELAY);
            this.idleTimeline.eventCallback(Constants.TWEEN_ONREPEAT, () => {
                const idleVO = voArray.shift();
                if (idleVO !== undefined) {
                    voArray.push(idleVO);
                    Application.instance.voiceover.playVO(idleVO, {priority: -2});
                }
            });
            this.idleTimeline.repeat(-1);
            this.idleTimeline.repeatDelay(IDLE_REPEAT_DELAY);
        }
        else {
            Application.instance.voiceover.playVO([VO.ONBOARDING.UI2, Constants.VO_SPACING], HLF.PlayMode.Append);
        }

        this.stamps.forEach((s) => s.interactive = false);
        this.markers.forEach((m) => m.interactive = false);
        this.swooshes.forEach((w) => w.interactive = false);

        this.animateInTimeline = new TimelineLite();
        // "For each city, the Marker should appear first."
        this.animateInTimeline
            .staggerFromTo(
                this.markers,
                0.75, // duration
                {pixi: {scaleX: 0, scaleY: 0}}, // from
                {
                    pixi: {scaleX: 1, scaleY: 1},  // to
                    ease: Elastic.easeOut,
                    onStart: () => {
                        Application.instance.audio.play("pluck", 1, false, HLF.AudioCategory.SFX);
                    },
                },
                0.075, // wait between each
                0, // start time
                () => {
                    // this.markers.forEach((b) => b.interactive = true);
                    // PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this.markers);
                },
            )
            // "Then .25s after the marker the swoosh should fade in and scale up"
            .staggerFromTo(
                this.swooshes,
                0.25, // duration
                {pixi: {scaleX: 0, scaleY: 0, alpha: 0}}, // from
                {pixi: {scaleX: 1, scaleY: 1, alpha: 1}}, // to
                0.075, // wait between each
                0.125, // start time
            )
            // "The stamp should fade in and scale up just after the swoosh starts animating in"
            .staggerFromTo(
                this.stamps,
                0.3, // duration
                {pixi: {scaleX: 0, scaleY: 0, alpha: 0}}, // from
                {
                    pixi: {scaleX: 1, scaleY: 1, alpha: 1}, // to
                    onStart: () => {
                        Application.instance.audio.play("pop-2", 1, false, HLF.AudioCategory.SFX);
                    },
                },
                0.075, // wait between each
                0.175, // start time
                () => {
                    this.EnableButtons();
                },
            )
            .delay(0.5) // wait for the loading screen to fade out
        ;

        const music = Application.instance.audio.getAudioTrack(bgm, HLF.AudioCategory.BGM);
        music?.setLooped(true);
        music?.setVolume(0.125);
        if (!(music?.isPlaying())) {
            music?.play();
        }
        else if (!music) {
            Application.instance.audio.play(bgm, 0.5, true, HLF.AudioCategory.BGM);
        }
    }

    public animateOut(pOnComplete: () => void) {
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        this.animateInTimeline?.kill();
        // const music = Application.instance.audio.getAudioTrack(bgm, HLF.AudioCategory.BGM);
        // music?.fadeTo(0, 500);
        super.animateOut(pOnComplete);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
    }

    protected EnableButtons() {
        const onboarding = GameState.Instance.onboarding;
        if (onboarding.complete) {
            this.stamps.forEach((b) => b.interactive = true);
        }
        else {
            const cb = () => {
                const onboardingTarget = this.stamps[0];
                onboardingTarget.interactive = true;
                TweenMax.to(onboardingTarget, 0.3, {pixi: {scale: 1.1}}).yoyo(true).repeat(3);
                const arrow = makeSprite("onboarding-arrow", ss);
                arrow.rotation = 180 * PIXI.DEG_TO_RAD;
                arrow.anchor.copy(arrow.texture.defaultAnchor);
                this.addChild(arrow);
                arrow.position.copy(onboardingTarget.position);
                arrow.position.y += 80;
                TweenLite.from(arrow, 0.5, {pixi: {alpha: 0}});
                TweenMax.to(arrow, 0.5, {pixi: {anchorY: 1.1}}).yoyo(true).repeat(-1);
            };
            if (Application.instance.skipVO) {
                cb();
            }
            else {
                Application.instance.voiceover.playVO([VO.ONBOARDING.O1, Constants.VO_SPACING],
                    HLF.PlayMode.Append, cb);
            }
        }
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this.stamps);
    }

    protected OnLevelSelectButtonClicked(pLevel: LocationID) {
        Application.instance.analytics?.event(
                "CitySelect",
                "LevelSelectButtonClicked",
                pLevel,
            );
        if (!GameState.Instance.onboarding.complete) {
                Application.instance.analytics?.event("tutorial_begin", "lunas-adventures");
            }
            // PubSub.publishSync(HLF.UNREGISTER_FOCUSABLES, this.markers);
        this.StartLevel(pLevel);
    }

    protected StartLevel(pLevel: LocationID) {
        GameState.Instance.GoToInterior(pLevel);
        // PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
        //     {id: Constants.STATE_LEVEL_SELECT, data: {firstTime: true}},
        //     Constants.LOAD_INTERSTITIAL, ...Constants.DEFAULT_TRANSITION,
        // ));
    }

    // TODO: extract to new class
    protected MakeButton(pFilename: string, pLevel?: LocationID, pHoverVO?: string) {
        return this.addChild(
            new CitySelectButton(pFilename, pLevel, this.OnLevelSelectButtonClicked.bind(this), pHoverVO));
    }

    protected RestartIdleTimer() {
        this.idleTimeline?.restart(true, true);
    }
}
